import type { SqliteDatabase } from './sqlite/database.js';
import {
  getSyncMetaVersion,
  setSyncMetaVersion,
  wipeOfflineDatabase,
} from './sqlite/database.js';
import {
  applyLocalStockDeltaSql,
  cacheBranchesSql,
  deleteCategory,
  deleteInventory,
  deleteProduct,
  getCachedBranchesSql,
  importJsonStateV2,
  loadCatalogSnapshot,
  searchProductsSql,
  setCatalogMeta,
  upsertCategory,
  upsertInventory,
  upsertProduct,
} from './sqlite/catalog.repo.js';
import {
  enqueueOutboxOp,
  listPendingOutboxOps,
  markOutboxPending,
  pendingOutboxCount,
  removeOutboxOp,
  resetInProgressToPending,
} from './sqlite/outbox.repo.js';
import {
  ackSyncedSaleInHistorySql,
  allocateLocalReceiptId,
  getBranchSalesHistorySql,
  getSalesHistoryEntryByCartId,
  markReceiptRefundedInHistorySql,
  mergePulledSalesHistoryEntry,
  resolveHistoryReceiptRefs,
  upsertSalesHistoryEntry,
} from './sqlite/history.repo.js';
import type {
  OfflineBranch,
  OfflineLoginCredentials,
  OfflinePosPersistedState,
  OfflineRefundPayload,
  OfflineSalePayload,
  OfflineAuthProfile,
  PendingOp,
} from './types.js';
import { hasSyncedCatalog } from './gates.js';
import { newClientOpId } from './pending-queue.js';
import { sanitizeRefundPayload, sanitizeSalePayload } from './payload-sanitize.js';
import { salePayloadToHistoryEntry } from './sales-history.js';
import {
  getAllBranchProducts,
  getLocalCategories,
  getProductsByCategory,
  searchLocalCatalogProducts,
  type LocalCategory,
  type LocalProductWithStock,
  type PaginatedLocalProducts,
} from './catalog-queries.js';
import {
  defaultOfflineAuthCrypto,
  type OfflineAuthCrypto,
} from './auth-crypto.js';
import {
  type CacheAuthProfileInput,
  findAuthProfile,
  removeAuthProfile as removeAuthProfileFromState,
  upsertAuthProfile,
  verifyOfflineLogin as verifyOfflineLoginState,
} from './auth-profiles.js';
import { SyncApiClient } from './sync-client.js';
import { SYNC_KEYS, type SyncKey } from './sync-keys-shared.js';
import type { SyncHttpClient } from './types.js';
import { createEmptyOfflineState } from './pending-queue.js';

export type { CacheAuthProfileInput };

export interface OfflineOrchestratorOptions {
  db: SqliteDatabase;
  http: SyncHttpClient;
  healthPath?: string;
  authCrypto?: OfflineAuthCrypto;
}

function saleEntityToHistory(entry: Record<string, unknown>, branchId: string) {
  const payment = entry.payment as {
    method: string;
    amountPaid: number;
    cashGiven?: number;
    cashReturned?: number;
  };
  return {
    id: String(entry.visibleId || entry.id),
    saleVisibleId: String(entry.visibleId || entry.id),
    receiptNumber: String(entry.receiptId),
    cartId: String(entry.cartId),
    branchId: String(entry.branchId || branchId),
    cashierName: String(
      (entry as { cashierName?: string }).cashierName ||
        (entry as { cashierId?: string }).cashierId ||
        '',
    ),
    items: entry.items as OfflineSalePayload['items'],
    subtotal: Number(entry.subtotal),
    discountPercentage: Number(entry.discountPercentage ?? 0),
    discountAmount: Number(entry.discountAmount ?? 0),
    grandTotal: Number(entry.totalAmount),
    paymentMethod: payment.method,
    cashGiven: payment.cashGiven ?? payment.amountPaid,
    cashReturned: payment.cashReturned ?? 0,
    createdAt: String(entry.createdAt),
    status: 'COMPLETED' as const,
    pendingSync: false,
  };
}

export class OfflineSyncOrchestrator {
  private readonly db: SqliteDatabase;
  private readonly http: SyncHttpClient;
  private readonly syncClient: SyncApiClient;
  private readonly healthPath: string;
  private readonly authCrypto: OfflineAuthCrypto;
  private online = true;
  private catalogRevision = 0;
  private syncInFlight: Promise<unknown> | null = null;
  private authState = createEmptyOfflineState();

  constructor(options: OfflineOrchestratorOptions) {
    this.db = options.db;
    this.http = options.http;
    this.syncClient = new SyncApiClient(options.http);
    this.healthPath = options.healthPath ?? '/api/health';
    this.authCrypto = options.authCrypto ?? defaultOfflineAuthCrypto;
    this.loadAuthStateFromDb();
  }

  private loadAuthStateFromDb(): void {
    const profiles = this.db.prepare(`SELECT * FROM auth_profiles`).all() as Array<{
      username: string;
      branch_id: string;
      branch_name: string | null;
      user_json: string;
      credential_verifier: string;
      encrypted_refresh_token: string | null;
      last_online_login_at: string;
    }>;
    this.authState.authProfiles = profiles.map((p) => ({
      username: p.username,
      branchId: p.branch_id,
      branchName: p.branch_name ?? undefined,
      user: JSON.parse(p.user_json),
      credentialVerifier: p.credential_verifier,
      encryptedRefreshToken: p.encrypted_refresh_token ?? undefined,
      lastOnlineLoginAt: p.last_online_login_at,
    }));
    this.authState.branches = getCachedBranchesSql(this.db);
  }

  private bumpRevision(): void {
    this.catalogRevision += 1;
  }

  private getCatalog(): ReturnType<typeof loadCatalogSnapshot> {
    const branchRow = this.db
      .prepare(`SELECT value FROM kv_meta WHERE key = 'catalog_branch_id'`)
      .get() as { value: string } | undefined;
    if (!branchRow?.value) return null;
    return loadCatalogSnapshot(this.db, branchRow.value);
  }

  hasCatalog(): boolean {
    return hasSyncedCatalog(this.getCatalog());
  }

  pendingCount(): number {
    return pendingOutboxCount(this.db);
  }

  getCatalogRevision(): number {
    return this.catalogRevision;
  }

  getLastSyncAt(): string | null {
    const row = this.db
      .prepare(`SELECT value FROM kv_meta WHERE key = 'last_sync_at'`)
      .get() as { value: string } | undefined;
    return row?.value ?? null;
  }

  isOnlineCached(): boolean {
    return this.online;
  }

  async checkOnline(): Promise<boolean> {
    try {
      await this.http.getJson(this.healthPath, '');
      this.online = true;
      this.db.prepare(
        `INSERT INTO kv_meta (key, value) VALUES ('last_online_at', ?)
         ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
      ).run(new Date().toISOString());
      return true;
    } catch {
      this.online = false;
      return false;
    }
  }

  static importJsonV2(db: SqliteDatabase, state: OfflinePosPersistedState): void {
    importJsonStateV2(db, state);
  }

  async pullDelta(
    accessToken: string,
    branchId: string,
    options?: { keys?: SyncKey[] },
  ): Promise<void> {
    const keys = options?.keys?.length ? options.keys : SYNC_KEYS;
    const versions: Partial<Record<SyncKey, number>> = {};
    for (const key of keys) {
      versions[key] = getSyncMetaVersion(this.db, key);
    }

    const pull = await this.syncClient.pull(accessToken, branchId, versions, options?.keys);

    for (const key of keys) {
      const delta = pull.data[key];
      if (!delta) continue;

      for (const id of delta.deletedIds ?? []) {
        await this.applyDeletion(key, id, branchId);
      }

      const applied = new Set<string>();
      if (Array.isArray(delta.entities) && delta.entities.length > 0) {
        for (const raw of delta.entities) {
          const entity = raw as Record<string, unknown>;
          const id = this.entityApplyId(key, entity, branchId);
          this.applyEntity(key, entity, branchId, id);
          applied.add(id);
          if (entity.visibleId) applied.add(String(entity.visibleId));
          if (entity.productId) {
            applied.add(String(entity.productId));
            applied.add(`${entity.branchId || branchId}:${entity.productId}`);
          }
        }
      }

      for (const id of delta.updatedIds ?? []) {
        if (applied.has(id)) continue;
        const entity = await this.syncClient.fetchEntity(accessToken, key, id, branchId);
        if (entity) {
          this.applyEntity(key, entity as Record<string, unknown>, branchId, id);
        }
      }
      setSyncMetaVersion(this.db, key, delta.version);
    }

    setCatalogMeta(this.db, branchId);
    this.bumpRevision();
  }

  private entityApplyId(
    key: SyncKey,
    entity: Record<string, unknown>,
    branchId: string,
  ): string {
    if (key === 'branchInventory') {
      const productId = String(entity.productId || '');
      return `${entity.branchId || branchId}:${productId}`;
    }
    return String(entity.visibleId || entity.id || '');
  }

  private applyDeletion(key: SyncKey, id: string, branchId: string): void {
    if (key === 'products') deleteProduct(this.db, id);
    if (key === 'categories') deleteCategory(this.db, id);
    if (key === 'branchInventory') {
      const [b, p] = id.split(':');
      deleteInventory(this.db, b || branchId, p || id);
    }
  }

  private applyEntity(
    key: SyncKey,
    entity: Record<string, unknown>,
    branchId: string,
    id: string,
  ): void {
    if (key === 'products') {
      upsertProduct(this.db, {
        visibleId: String(entity.visibleId || entity.id || id),
        name: String(entity.name),
        sku: String(entity.sku),
        barcode: (entity.barcode as string) ?? null,
        description: (entity.description as string) ?? null,
        salePriceHT: Number(entity.salePriceHT),
        taxRate: Number(entity.taxRate),
        categoryId: String(entity.categoryId),
        isActive: entity.isActive !== false,
      });
    } else if (key === 'categories') {
      upsertCategory(this.db, {
        visibleId: String(entity.visibleId || entity.id || id),
        name: String(entity.name),
        isActive: entity.isActive !== false,
        isFreeSaleOrService: entity.isFreeSaleOrService === true,
        isFreePrice: entity.isFreePrice === true,
      });
    } else if (key === 'branchInventory') {
      upsertInventory(this.db, {
        productId: String(entity.productId),
        branchId: String(entity.branchId || branchId),
        stock: Number(entity.stock ?? 0),
        exposedOnSite: entity.exposedOnSite as number | undefined,
      });
    } else if (key === 'branches') {
      this.db.prepare(
        `INSERT OR REPLACE INTO branches (visible_id, name, code, address, is_main_branch)
         VALUES (?, ?, ?, ?, ?)`,
      ).run(
        String(entity.visibleId || entity.id || id),
        String(entity.name),
        String(entity.code),
        (entity.address as string) ?? null,
        entity.isMainBranch ? 1 : 0,
      );
    } else if (key === 'salesHistory') {
      const hist = saleEntityToHistory(entity, branchId);
      mergePulledSalesHistoryEntry(this.db, hist);
    } else if (key === 'refunds') {
      const originalReceiptId = String(entity.originalReceiptId);
      markReceiptRefundedInHistorySql(this.db, originalReceiptId);
    }
  }

  async pushPending(accessToken: string): Promise<{ pushed: number; failed: number }> {
    resetInProgressToPending(this.db);
    let pushed = 0;
    let failed = 0;

    const saleResult = await this.pushPendingKind(accessToken, 'sale');
    pushed += saleResult.pushed;
    failed += saleResult.failed;

    const refundResult = await this.pushPendingKind(accessToken, 'refund');
    pushed += refundResult.pushed;
    failed += refundResult.failed;

    if (pushed > 0) {
      this.db.prepare(
        `INSERT INTO kv_meta (key, value) VALUES ('last_sync_at', ?)
         ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
      ).run(new Date().toISOString());
      this.bumpRevision();
    }

    return { pushed, failed };
  }

  private async pushPendingKind(
    accessToken: string,
    kind: 'sale' | 'refund',
  ): Promise<{ pushed: number; failed: number }> {
    const ops = listPendingOutboxOps(this.db).filter((op) => op.kind === kind);
    if (ops.length === 0) return { pushed: 0, failed: 0 };

    const operations = ops.map((op) => {
      const payload = JSON.parse(op.payloadJson) as Record<string, unknown>;
      if (kind === 'refund') {
        const resolved = resolveHistoryReceiptRefs(
          this.db,
          String(payload.originalReceiptId ?? ''),
          String(payload.originalSaleId ?? ''),
        );
        if (
          payload.originalReceiptId !== resolved.receiptNumber ||
          payload.originalSaleId !== resolved.saleVisibleId
        ) {
          payload.originalReceiptId = resolved.receiptNumber;
          payload.originalSaleId = resolved.saleVisibleId;
          this.db
            .prepare(
              `UPDATE outbox_ops SET payload_json = ?, cart_id = ? WHERE client_op_id = ?`,
            )
            .run(JSON.stringify(payload), resolved.receiptNumber, op.clientOpId);
        }
      }
      return {
        clientOpId: op.clientOpId,
        kind: op.kind,
        payload,
      };
    });

    let pushed = 0;
    let failed = 0;

    try {
      const { results } = await this.syncClient.push(accessToken, operations);
      for (const result of results) {
        const op = ops.find((o) => o.clientOpId === result.clientOpId);
        if (!op) continue;
        if (result.status === 'completed') {
          if (result.sale) {
            ackSyncedSaleInHistorySql(this.db, result.sale.cartId, {
              receiptId: result.sale.receiptId,
              saleVisibleId: result.sale.visibleId,
            });
          }
          removeOutboxOp(this.db, op.clientOpId);
          pushed += 1;
        } else if (result.status === 'conflict' && result.sale) {
          ackSyncedSaleInHistorySql(this.db, result.sale.cartId, {
            receiptId: result.sale.receiptId,
            saleVisibleId: result.sale.visibleId,
          });
          removeOutboxOp(this.db, op.clientOpId);
          pushed += 1;
        } else {
          markOutboxPending(this.db, op.clientOpId, result.error);
          failed += 1;
        }
      }
    } catch (error) {
      for (const op of ops) {
        markOutboxPending(
          this.db,
          op.clientOpId,
          error instanceof Error ? error.message : String(error),
        );
      }
      failed = ops.length;
    }

    return { pushed, failed };
  }

  async syncCycle(accessToken: string, branchId: string) {
    if (this.syncInFlight) return this.syncInFlight as ReturnType<typeof this.runSyncCycle>;

    this.syncInFlight = this.runSyncCycle(accessToken, branchId);
    try {
      return await this.syncInFlight;
    } finally {
      this.syncInFlight = null;
    }
  }

  private async runSyncCycle(accessToken: string, branchId: string) {
    const online = await this.checkOnline();
    if (!online) {
      return {
        online: false,
        pushed: 0,
        failed: 0,
        catalogReady: this.hasCatalog(),
        catalogRevision: this.catalogRevision,
      };
    }
    const { pushed, failed } = await this.pushPending(accessToken);
    await this.pullDelta(accessToken, branchId);
    return {
      online: true,
      pushed,
      failed,
      catalogReady: this.hasCatalog(),
      catalogRevision: this.catalogRevision,
    };
  }

  /** @deprecated alias — use pullDelta */
  async pullCatalog(accessToken: string, branchId: string) {
    await this.pullDelta(accessToken, branchId);
    const catalog = this.getCatalog();
    if (!catalog) throw new Error('Catalog not available after pull');
    return catalog;
  }

  async pullSalesHistory(accessToken: string, branchId: string) {
    await this.pullDelta(accessToken, branchId, { keys: ['salesHistory', 'refunds'] });
    return getBranchSalesHistorySql(this.db, branchId).length;
  }

  getSalesHistory(branchId: string) {
    return getBranchSalesHistorySql(this.db, branchId);
  }

  enqueueSale(
    payload: OfflineSalePayload,
    localReceiptId?: string,
    options?: { cashierName?: string },
  ): PendingOp {
    const clean = sanitizeSalePayload(payload);
    const clientOpId = newClientOpId('sale');
    const receiptId = localReceiptId || allocateLocalReceiptId(this.db);
    const op: PendingOp = {
      clientOpId,
      kind: 'sale',
      createdAt: new Date().toISOString(),
      payload: clean,
      attempts: 0,
      localReceiptId: receiptId,
    };

    const tx = this.db.transaction(() => {
      enqueueOutboxOp(this.db, {
        clientOpId,
        kind: 'sale',
        cartId: clean.cartId,
        payloadJson: JSON.stringify(clean),
        localReceiptId: receiptId,
      });
      applyLocalStockDeltaSql(this.db, clean.branchId, clean.items, 'sale');
      if (options?.cashierName) {
        upsertSalesHistoryEntry(
          this.db,
          salePayloadToHistoryEntry(clean, receiptId, options.cashierName, {
            pendingSync: true,
          }),
        );
      }
    });
    tx();
    this.bumpRevision();
    return op;
  }

  enqueueRefund(payload: OfflineRefundPayload): PendingOp {
    const settled = payload.settled !== false;
    const sanitized = sanitizeRefundPayload(payload);
    const resolved = resolveHistoryReceiptRefs(
      this.db,
      sanitized.originalReceiptId,
      sanitized.originalSaleId,
    );
    const clean: OfflineRefundPayload = {
      ...sanitized,
      originalReceiptId: resolved.receiptNumber,
      originalSaleId: resolved.saleVisibleId,
    };
    const clientOpId = newClientOpId('refund');
    const op: PendingOp = {
      clientOpId,
      kind: 'refund',
      createdAt: new Date().toISOString(),
      payload: clean,
      attempts: 0,
    };

    const tx = this.db.transaction(() => {
      enqueueOutboxOp(this.db, {
        clientOpId,
        kind: 'refund',
        cartId: clean.originalReceiptId,
        payloadJson: JSON.stringify(clean),
      });
      applyLocalStockDeltaSql(this.db, clean.branchId, clean.items, 'refund');
      if (clean.exchanges && clean.exchanges.length > 0) {
        applyLocalStockDeltaSql(
          this.db,
          clean.branchId,
          clean.exchanges.map((line) => ({
            productId: line.replacementProductId,
            quantity: line.quantity,
          })),
          'sale',
        );
      }
      markReceiptRefundedInHistorySql(
        this.db,
        clean.originalReceiptId,
        clean.originalSaleId,
        settled ? 'REFUNDED' : 'PARTIAL',
        clean.items,
      );
    });
    tx();
    this.bumpRevision();
    return op;
  }

  async submitSaleLocalFirst(
    payload: OfflineSalePayload,
    accessToken: string,
    localReceiptId?: string,
    options?: { cashierName?: string },
  ) {
    const receiptId = localReceiptId || allocateLocalReceiptId(this.db);
    const pending = this.enqueueSale(payload, receiptId, options);
    const online = await this.checkOnline();
    if (!online) return { mode: 'offline' as const, receiptId, pending };
    const before = this.pendingCount();
    await this.pushPending(accessToken);
    const pushed = before > this.pendingCount();
    const synced = getSalesHistoryEntryByCartId(this.db, payload.cartId);
    const finalReceiptId = synced?.receiptNumber ?? receiptId;
    return {
      mode: pushed ? ('online' as const) : ('offline' as const),
      receiptId: finalReceiptId,
      pending,
    };
  }

  async submitRefundLocalFirst(payload: OfflineRefundPayload, accessToken: string) {
    const pending = this.enqueueRefund(payload);
    const online = await this.checkOnline();
    if (!online) return { mode: 'offline' as const, pending };
    const before = this.pendingCount();
    await this.pushPending(accessToken);
    const pushed = before > this.pendingCount();
    return { mode: pushed ? ('online' as const) : ('offline' as const), pending };
  }

  searchLocalProducts(query: string, branchId?: string): LocalProductWithStock[] {
    const catalog = this.getCatalog();
    const bid = branchId || catalog?.branchId || '';
    if (!query.trim()) {
      return searchLocalCatalogProducts(catalog, '', bid);
    }
    const products = searchProductsSql(this.db, query, bid);
    const invMap = new Map(
      (catalog?.inventories ?? []).map((i) => [i.productId, i.stock]),
    );
    return products.map((p) => ({
      ...p,
      stock: invMap.get(p.visibleId) ?? 0,
      exposedOnSite: invMap.get(p.visibleId) ?? 0,
      availableStock: invMap.get(p.visibleId) ?? 0,
    }));
  }

  getLocalCategoriesList(): LocalCategory[] {
    return getLocalCategories(this.getCatalog());
  }

  getLocalProductsByCategory(
    categoryId: string,
    branchId: string,
    page = 1,
    limit = 50,
  ): PaginatedLocalProducts {
    return getProductsByCategory(this.getCatalog(), categoryId, branchId, page, limit);
  }

  getAllLocalBranchProducts(branchId: string, page = 1, limit = 100): PaginatedLocalProducts {
    return getAllBranchProducts(this.getCatalog(), branchId, page, limit);
  }

  getLocalStock(productId: string, branchId: string): number {
    const row = this.db
      .prepare(
        `SELECT stock FROM branch_inventory WHERE branch_id = ? AND product_id = ?`,
      )
      .get(branchId, productId) as { stock: number } | undefined;
    return row?.stock ?? 0;
  }

  cacheBranches(branches: OfflineBranch[]): void {
    cacheBranchesSql(this.db, branches);
    this.authState.branches = branches;
    this.bumpRevision();
  }

  getCachedBranches(): OfflineBranch[] {
    return getCachedBranchesSql(this.db);
  }

  async cacheAuthProfile(input: CacheAuthProfileInput): Promise<OfflineAuthProfile | null> {
    if (!input.rememberMe) return null;
    this.authState = await upsertAuthProfile(this.authState, input, this.authCrypto);
    const profile = findAuthProfile(this.authState, input.username, input.branchId);
    if (profile) {
      this.db.prepare(
        `INSERT OR REPLACE INTO auth_profiles (
          username, branch_id, branch_name, user_json, credential_verifier,
          encrypted_refresh_token, last_online_login_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?)`,
      ).run(
        profile.username,
        profile.branchId,
        profile.branchName ?? null,
        JSON.stringify(profile.user),
        profile.credentialVerifier,
        profile.encryptedRefreshToken ?? null,
        profile.lastOnlineLoginAt,
      );
    }
    this.bumpRevision();
    return profile;
  }

  findAuthProfile(username: string, branchId: string): OfflineAuthProfile | null {
    return findAuthProfile(this.authState, username, branchId);
  }

  async verifyOfflineLogin(credentials: OfflineLoginCredentials) {
    return verifyOfflineLoginState(this.authState, credentials, this.authCrypto);
  }

  async unsealProfileRefreshToken(profile: OfflineAuthProfile): Promise<string | null> {
    if (!profile.encryptedRefreshToken) return null;
    return this.authCrypto.unsealRefreshToken(profile.encryptedRefreshToken);
  }

  removeAuthProfile(username: string, branchId: string): void {
    this.authState = removeAuthProfileFromState(this.authState, username, branchId);
    this.db.prepare(`DELETE FROM auth_profiles WHERE username = ? AND branch_id = ?`).run(
      username,
      branchId,
    );
    this.bumpRevision();
  }

  /** Terminal reset: drop catalog, pending sales, and saved cashier profiles. */
  wipeLocalData(): void {
    wipeOfflineDatabase(this.db);
    this.authState = createEmptyOfflineState();
    this.bumpRevision();
  }

  getAuthProfileCount(): number {
    return this.authState.authProfiles.length;
  }

  /** Compatibility shim for gates that read persisted state shape */
  getState() {
    return {
      version: 2 as const,
      catalog: this.getCatalog(),
      pending: listPendingOutboxOps(this.db).map((op) => ({
        clientOpId: op.clientOpId,
        kind: op.kind,
        createdAt: op.createdAt,
        payload: JSON.parse(op.payloadJson),
        attempts: op.failedAttempts,
        lastError: op.lastError ?? undefined,
        localReceiptId: op.localReceiptId ?? undefined,
      })),
      branches: this.getCachedBranches(),
      authProfiles: this.authState.authProfiles,
      salesHistory: getBranchSalesHistorySql(
        this.db,
        (
          this.db
            .prepare(`SELECT value FROM kv_meta WHERE key = 'catalog_branch_id'`)
            .get() as { value: string } | undefined
        )?.value ?? '',
      ),
      lastOnlineAt:
        (
          this.db
            .prepare(`SELECT value FROM kv_meta WHERE key = 'last_online_at'`)
            .get() as { value: string } | undefined
        )?.value ?? null,
      lastSyncAt: this.getLastSyncAt(),
    };
  }
}
